import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getSyncEvents, getSyncEvent } from '../api';
import { formatTimestamp, formatRelativeTime, getEventTypeLabel, getEventTypeIcon } from '../utils';
import { getClientIpFromHeaders, isDeviceLinkConflictRequestSummary } from '../utils/deviceLinkConflict';
import { useFilters } from '../hooks';
import type {
  SyncEvent,
  SyncEventFilters,
  DeviceLinkConflictResponseSummary,
} from '../types';
import { EVENT_TYPES } from '../types';
import styles from './DeviceLinkConflicts.module.css';

type DrawerMode = 'detail' | 'deviceHistory';

export default function DeviceLinkConflicts() {
  const navigate = useNavigate();
  const { timeRange, setTimeRange } = useFilters();

  const [events, setEvents] = useState<SyncEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedDeviceCode, setSelectedDeviceCode] = useState<string | null>(null);
  const [detailEvent, setDetailEvent] = useState<SyncEvent | null>(null);
  const [deviceHistory, setDeviceHistory] = useState<SyncEvent[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const buildTimeFilter = useCallback((): Pick<SyncEventFilters, 'timestamp_gte'> | Record<string, never> => {
    if (!timeRange) return {};
    const now = new Date();
    let since: Date;
    switch (timeRange) {
      case '1h':
        since = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '24h':
        since = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        return {};
    }
    return { timestamp_gte: since.toISOString() };
  }, [timeRange]);

  const fetchConflicts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const filters: SyncEventFilters = {
        event_type: EVENT_TYPES.DEVICE_LINK_CONFLICT,
        ordering: '-timestamp',
        page,
        page_size: pageSize,
        success: false,
        ...buildTimeFilter(),
      };

      if (search.trim()) {
        const q = search.trim();
        if (q.includes('@')) {
          filters.user_email = q;
        } else {
          filters.device_code = q.toUpperCase();
        }
      }

      const response = await getSyncEvents(filters);
      const results = (response.results || []).filter(
        (e) => e.event_type === EVENT_TYPES.DEVICE_LINK_CONFLICT
      );
      setEvents(results);
      setTotalCount(response.count ?? results.length);
    } catch (err) {
      console.error('[DeviceLinkConflicts] Error:', err);
      setError('Failed to load device link conflict events');
    } finally {
      setIsLoading(false);
    }
  }, [page, search, buildTimeFilter]);

  useEffect(() => {
    fetchConflicts();
  }, [fetchConflicts]);

  useEffect(() => {
    setPage(1);
  }, [search, timeRange]);

  const closeDrawer = () => {
    setDrawerMode(null);
    setSelectedEventId(null);
    setSelectedDeviceCode(null);
    setDetailEvent(null);
    setDeviceHistory([]);
  };

  const openDetailDrawer = async (eventId: string) => {
    setDrawerMode('detail');
    setSelectedEventId(eventId);
    setSelectedDeviceCode(null);
    setDetailEvent(null);
    setDeviceHistory([]);
    setDrawerLoading(true);

    try {
      const full = await getSyncEvent(eventId);
      setDetailEvent(full);
      if (full.device_code) {
        setSelectedDeviceCode(full.device_code);
      }
    } catch (err) {
      console.error('[DeviceLinkConflicts] Detail load failed:', err);
    } finally {
      setDrawerLoading(false);
    }
  };

  const openDeviceHistory = async (deviceCode: string, conflictEventId?: string) => {
    setDrawerMode('deviceHistory');
    setSelectedDeviceCode(deviceCode);
    setSelectedEventId(conflictEventId ?? null);
    setDetailEvent(null);
    setDrawerLoading(true);

    try {
      const [historyResponse, detail] = await Promise.all([
        getSyncEvents({
          device_code: deviceCode,
          ordering: '-timestamp',
          page_size: 100,
        }),
        conflictEventId ? getSyncEvent(conflictEventId).catch(() => null) : Promise.resolve(null),
      ]);
      setDeviceHistory(historyResponse.results || []);
      if (detail) {
        setDetailEvent(detail);
        setDrawerMode('detail');
        setSelectedEventId(conflictEventId ?? null);
      }
    } catch (err) {
      console.error('[DeviceLinkConflicts] Device history failed:', err);
    } finally {
      setDrawerLoading(false);
    }
  };

  const loadDeviceHistoryInDetail = async (deviceCode: string) => {
    setDrawerLoading(true);
    try {
      const historyResponse = await getSyncEvents({
        device_code: deviceCode,
        ordering: '-timestamp',
        page_size: 100,
      });
      setDeviceHistory(historyResponse.results || []);
      setDrawerMode('deviceHistory');
      setSelectedDeviceCode(deviceCode);
    } finally {
      setDrawerLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const renderConflictParties = (event: SyncEvent) => {
    const request = event.request_summary;
    if (!isDeviceLinkConflictRequestSummary(request)) {
      return (
        <p className={styles.drawerSubtitle}>
          Owner details require the detail endpoint — open full event if summary is missing.
        </p>
      );
    }
    return (
      <>
        <div className={`${styles.partyCard} ${styles.partyCardOwner}`}>
          <div className={styles.partyLabel}>Existing owner (linked account)</div>
          <div className={styles.partyEmail}>{request.existing_owner_email}</div>
          <div className={styles.partyId}>{request.existing_owner_user_id}</div>
        </div>
        <div className={`${styles.partyCard} ${styles.partyCardAttempter}`}>
          <div className={styles.partyLabel}>Attempted link (blocked)</div>
          <div className={styles.partyEmail}>{request.attempted_by_email}</div>
          <div className={styles.partyId}>{request.attempted_by_user_id}</div>
        </div>
      </>
    );
  };

  const uniqueIpsFromHistory = [...new Set(
    deviceHistory
      .map((e) => getClientIpFromHeaders(e.request_headers))
      .filter((ip): ip is string => Boolean(ip))
  )];

  const drawerOpen = drawerMode !== null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <span className={styles.titleIcon}>⚠️</span>
            Device Link Conflicts
          </h1>
          <p className={styles.subtitle}>
            Possible device ID collisions — when a second user tries to link a 6-character ID already
            owned by another account. Sync is not blocked; only Studio linking returns 409.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.refreshButton}
            onClick={fetchConflicts}
            disabled={isLoading}
            type="button"
          >
            <span className={isLoading ? styles.spinning : ''}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.notice}>
        Events appear only after contested-device logging was deployed. Older collisions are not
        backfilled. The contested flag on devices is not exposed via API yet — this view uses{' '}
        <code>device_link_conflict</code> sync events only.
      </div>

      <div className={styles.filters}>
        <input
          type="text"
          placeholder="Search by attempter email or device code (e.g. 108AE4)..."
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={styles.select}
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
        >
          <option value="">All Time</option>
          <option value="1h">Last Hour</option>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.tableContainer}>
        {isLoading && events.length === 0 ? (
          <div className={styles.loading}>Loading conflict events...</div>
        ) : events.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>✨</div>
            <h3>No device link conflicts</h3>
            <p>
              No <code>device_link_conflict</code> events match your filters. That is expected until
              the next incident after logging was enabled.
            </p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th>
                <th>Device ID</th>
                <th>Attempter</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr
                  key={event.id}
                  className={styles.clickableRow}
                  onClick={() => openDetailDrawer(event.id)}
                >
                  <td>
                    <div className={styles.timestamp}>
                      <span>{formatTimestamp(event.timestamp)}</span>
                      <span className={styles.timestampRelative}>
                        {formatRelativeTime(event.timestamp)}
                      </span>
                    </div>
                  </td>
                  <td>
                    {event.device_code ? (
                      <button
                        type="button"
                        className={`${styles.deviceCode} ${styles.deviceCodeButton}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeviceHistory(event.device_code!, event.id);
                        }}
                        title="View sync history for this device ID"
                      >
                        {event.device_code}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{event.user_email || '—'}</td>
                  <td>
                    <span className={styles.conflictBadge}>409 Conflict</span>
                  </td>
                  <td>
                    <Link
                      to={`/events/${event.id}`}
                      className={styles.viewLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Full event →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageButton}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ← Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages} ({totalCount.toLocaleString()} total)
          </span>
          <button
            type="button"
            className={styles.pageButton}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next →
          </button>
        </div>
      )}

      {drawerOpen && (
        <>
          <div className={styles.drawerOverlay} onClick={closeDrawer} role="presentation" />
          <aside
            className={`${styles.drawer} ${drawerMode === 'deviceHistory' ? styles.drawerWide : ''}`}
            role="dialog"
            aria-label={
              drawerMode === 'deviceHistory' ? 'Device sync history' : 'Conflict detail'
            }
          >
            <div className={styles.drawerHeader}>
              <div>
                <h2 className={styles.drawerTitle}>
                  {drawerMode === 'deviceHistory'
                    ? `Device ${selectedDeviceCode}`
                    : 'Link conflict detail'}
                </h2>
                <p className={styles.drawerSubtitle}>
                  {drawerMode === 'deviceHistory'
                    ? 'Recent sync events for triage (IPs, firmware, auth)'
                    : detailEvent?.device_code
                      ? `Device ${detailEvent.device_code}`
                      : 'Loading…'}
                </p>
              </div>
              <button type="button" className={styles.closeButton} onClick={closeDrawer}>
                ✕
              </button>
            </div>

            <div className={styles.drawerBody}>
              {drawerLoading ? (
                <div className={styles.drawerLoading}>Loading…</div>
              ) : drawerMode === 'detail' && detailEvent ? (
                <>
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Accounts</h3>
                    {renderConflictParties(detailEvent)}
                  </div>

                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Outcome</h3>
                    {(() => {
                      const response = detailEvent.response_summary as
                        | DeviceLinkConflictResponseSummary
                        | undefined;
                      const listAttempter = detailEvent.user_email;
                      return (
                        <>
                          <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>HTTP status</span>
                            <span className={styles.infoValue}>
                              {detailEvent.status_code ?? 409}
                            </span>
                          </div>
                          {response && (
                            <div className={styles.infoRow}>
                              <span className={styles.infoLabel}>Sync blocked</span>
                              <span className={styles.infoValue}>
                                {response.blocked ? 'Yes' : 'No'}
                              </span>
                            </div>
                          )}
                          {listAttempter && (
                            <div className={styles.infoRow}>
                              <span className={styles.infoLabel}>List attempter</span>
                              <span className={styles.infoValue}>{listAttempter}</span>
                            </div>
                          )}
                          <div className={styles.syncNote} style={{ marginTop: '0.75rem' }}>
                            Studio link was rejected (409). Device sync was not stopped — only
                            linking is blocked until support resolves the ID collision.
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Client IP (this event)</h3>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>IP</span>
                      <span className={styles.infoValue}>
                        {getClientIpFromHeaders(detailEvent.request_headers) || '—'}
                      </span>
                    </div>
                  </div>

                  <div className={styles.drawerActions}>
                    {detailEvent.device_code && (
                      <button
                        type="button"
                        className={styles.actionButton}
                        onClick={() =>
                          loadDeviceHistoryInDetail(detailEvent.device_code!)
                        }
                      >
                        Device sync history →
                      </button>
                    )}
                    <Link
                      to={`/events?device_code=${encodeURIComponent(detailEvent.device_code || '')}`}
                      className={styles.actionButton}
                    >
                      All events (filtered) →
                    </Link>
                    <button
                      type="button"
                      className={styles.actionButton}
                      onClick={() => navigate(`/events/${detailEvent.id}`)}
                    >
                      Full event page →
                    </button>
                  </div>
                </>
              ) : drawerMode === 'deviceHistory' ? (
                <>
                  {uniqueIpsFromHistory.length > 0 && (
                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>Distinct client IPs</h3>
                      <div className={styles.ipList}>
                        {uniqueIpsFromHistory.map((ip) => (
                          <span key={ip} className={styles.ipTag}>
                            {ip}
                          </span>
                        ))}
                      </div>
                      <p className={styles.drawerSubtitle} style={{ marginTop: '0.5rem' }}>
                        Multiple IPs on the same device ID may indicate a collision.
                      </p>
                    </div>
                  )}

                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                      Events ({deviceHistory.length})
                    </h3>
                    {deviceHistory.length === 0 ? (
                      <p className={styles.drawerSubtitle}>No events for this device code.</p>
                    ) : (
                      <table className={styles.historyTable}>
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Type</th>
                            <th>User</th>
                            <th>IP</th>
                            <th>Firmware</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deviceHistory.map((e) => (
                            <tr key={e.id}>
                              <td title={formatTimestamp(e.timestamp)}>
                                {formatRelativeTime(e.timestamp)}
                              </td>
                              <td>
                                {getEventTypeIcon(e.event_type)}{' '}
                                {getEventTypeLabel(e.event_type)}
                              </td>
                              <td>{e.user_email || '—'}</td>
                              <td>{getClientIpFromHeaders(e.request_headers) || '—'}</td>
                              <td>{e.firmware_version || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div className={styles.drawerActions}>
                    {selectedEventId && (
                      <button
                        type="button"
                        className={styles.actionButton}
                        onClick={() => openDetailDrawer(selectedEventId)}
                      >
                        ← Back to conflict
                      </button>
                    )}
                    <Link
                      to={`/events?device_code=${encodeURIComponent(selectedDeviceCode || '')}`}
                      className={styles.actionButton}
                    >
                      Open in Sync Events →
                    </Link>
                  </div>
                </>
              ) : (
                <div className={styles.drawerLoading}>Could not load drawer content.</div>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
